import React, { useRef } from "react";
import {
  EmptyState,
  IconButton,
  Table,
  TableHeader,
  TableProps,
  TableReloadHandle,
  TableRowItem,
  TableSortOption,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { useTranslation } from "react-i18next";
import { SearchX, SquarePen, Trash2 } from "lucide-react";
import {
  useDeleteEmailTemplateStore,
  useEmailTemplatesStore,
} from "../../stores/Hooks";
import { EMAIL_TEMPLATES_PER_PAGE } from "../../stores/EmailTemplatesStore";
import { GetEmailTemplatesRPC } from "@pulse/pulse-rpcs/lib/email-cycle/getEmailTemplatesRPC";
import { StyledEllipsisTypography } from "../../../../components/StyledEllipsisTypography";
import { Instance } from "mobx-state-tree";
import {
  EmailTemplateTableFilter,
  EmailTemplateTableFilterType,
} from "./filters/EmailTemplateTableFilter";
import { EMPTY_CHARACTER } from "@pulse/shared-components";
import { Stack } from "@mui/material";
import { observer } from "mobx-react";
import { TypesSortOrder } from "@pulse/pulse-rpcs/lib/types/typesSortOrder";
import { getSortOrder } from "../../../utils/getSortOrder";
import { EmailTemplateModel } from "../../models/EmailTemplateModel";
import { useSearchParams } from "react-router-dom";
import { RouteSearchParam } from "../../../../routes/RoutesHelper";
import { Mode } from "../../../enums/Mode";
import { useSignInStore } from "../../../store/Hooks";
import { UserPrivileges } from "../../../store/user/UserPrivileges";

const TEMPLATE_NAME_COLUMN_WIDTH = "540px";
const PROJECT_NAME_COLUMN_WIDTH = "356px";
const ACTION_COLUMN_WIDTH = "128px";
const TABLE_WIDTH = `calc(${TEMPLATE_NAME_COLUMN_WIDTH} + ${PROJECT_NAME_COLUMN_WIDTH} + ${ACTION_COLUMN_WIDTH})`;

enum EmailTemplatesTableColumn {
  TemplateName = "TEMPLATE_NAME",
  ProjectName = "PROJECT_NAME",
  Actions = "ACTIONS",
}

interface SortOrderAndColumn {
  order: TypesSortOrder.TypesSortOrder;
  column: GetEmailTemplatesRPC.RequestEnums.SortColumn.SortColumn;
}

interface EmailTemplateTableProps {
  setOpenConfirmDeleteEmailTemplateDialog: React.Dispatch<
    React.SetStateAction<boolean>
  >;
}

export const EmailTemplatesTable: React.FC<EmailTemplateTableProps> = observer(
  ({ setOpenConfirmDeleteEmailTemplateDialog }) => {
    const [, setSearchParams] = useSearchParams();

    const { t } = useTranslation();
    const spacing = useSpacing();
    const typography = useTypography();
    const emailTemplatesStore = useEmailTemplatesStore();
    const isInitialLoad = useRef(true);
    const emailTemplateTableRef = useRef<TableReloadHandle | null>(null);
    const signInStore = useSignInStore();
    const deleteEmailTemplateStore = useDeleteEmailTemplateStore();

    const emailTemplateTableHeaders: TableHeader = [
      {
        columnId: EmailTemplatesTableColumn.TemplateName,
        label: t("emailTemplates.templateNameColumnLabel"),
        width: TEMPLATE_NAME_COLUMN_WIDTH,
        sortable: true,
      },
      {
        columnId: EmailTemplatesTableColumn.ProjectName,
        label: t("emailTemplates.projectNameColumnLabel"),
        width: PROJECT_NAME_COLUMN_WIDTH,
      },
      {
        columnId: EmailTemplatesTableColumn.Actions,
        label: EMPTY_CHARACTER,
        width: ACTION_COLUMN_WIDTH,
      },
    ];

    const paginationOption: TableProps["paginationOption"] = {
      itemsPerPage: EMAIL_TEMPLATES_PER_PAGE,
      getPageIndicatorText: (startItem, endItem, totalItems): string =>
        t("common.paginationIndicationText", {
          startItem,
          endItem,
          totalItems,
        }),
    };

    const viewOverrides: TableProps["viewOverrides"] = {
      empty: {
        icon: (
          <EmptyState
            icon={<SearchX />}
            title={t("emailTemplates.emptyTemplatesMessage")}
            description={t("emailTemplates.emptySearchResultMessage")}
            width="100%"
          />
        ),
        message: EMPTY_CHARACTER,
      },
      loading: {
        message: t("common.searchTableLoadingState"),
      },
    };

    const showActionButtons = signInStore.isPrivilegeGranted(
      UserPrivileges.processGlobalEmailTemplate,
    );

    const getEmailTemplateTableCells = (
      emailTemplate: Instance<typeof EmailTemplateModel>,
    ): TableRowItem["cells"] => [
      {
        columnId: EmailTemplatesTableColumn.TemplateName,
        data: (
          <StyledEllipsisTypography
            data={emailTemplate.templateName.name}
            typography={typography}
            t={t}
          />
        ),
      },
      {
        columnId: EmailTemplatesTableColumn.ProjectName,
        data: (
          <StyledEllipsisTypography
            data={emailTemplate.projectName ?? null}
            typography={typography}
            t={t}
          />
        ),
      },
      {
        columnId: EmailTemplatesTableColumn.Actions,
        data: showActionButtons ? (
          <Stack direction="row" justifyContent="end" width="100%">
            <IconButton
              name="editEmailTemplateButton"
              size="small"
              icon={<SquarePen />}
              variant="plain-neutral"
              onClick={() => {
                setSearchParams((prev) => {
                  prev.set(RouteSearchParam.TemplateId, emailTemplate.id);
                  prev.set(RouteSearchParam.Mode, Mode.Edit);
                  return prev;
                });
              }}
              hoverColor="buttonOnSurfaceIcon"
            />
            <IconButton
              name="deleteEmailTemplateButton"
              size="small"
              icon={<Trash2 />}
              variant="plain-color"
              onClick={() => {
                deleteEmailTemplateStore.setTemplateId(emailTemplate.id);
                deleteEmailTemplateStore.emailTemplateName.setName(
                  emailTemplate.templateName.name,
                );
                setOpenConfirmDeleteEmailTemplateDialog(true);
              }}
              color="destructive"
            />
          </Stack>
        ) : (
          <></>
        ),
      },
    ];

    const getSortColumn = (columnId: string): SortOrderAndColumn["column"] => {
      switch (columnId) {
        case EmailTemplatesTableColumn.TemplateName:
          return GetEmailTemplatesRPC.RequestEnums.SortColumn.SortColumn
            .TEMPLATE_NAME;
        default:
          throw new Error(`Invalid columnId: ${columnId}`);
      }
    };

    const getSortOrderAndColumn = (
      sort: TableSortOption | undefined,
    ): SortOrderAndColumn => {
      if (!sort) {
        return {
          order: TypesSortOrder.TypesSortOrder.DESCENDING,
          column:
            GetEmailTemplatesRPC.RequestEnums.SortColumn.SortColumn.MODIFIED_AT,
        };
      }

      return {
        order: getSortOrder(sort.order),
        column: getSortColumn(sort.id),
      };
    };

    const onTableOptionsChange: TableProps<EmailTemplateTableFilterType>["onTableOptionsChange"] =
      async (options, setTotalItems) => {
        const { order, column } = getSortOrderAndColumn(options.sort);
        const filter = options.filter ?? { value: EMPTY_CHARACTER };
        const pageIndex = options.pageNumber ?? 1;

        if (isInitialLoad.current) {
          // Skip calling GetEmailTemplatesRPC on initial load
          // since we are calling it in EmailTemplatesPage.tsx
          // on initial load of the page.
          isInitialLoad.current = false;
        } else {
          await emailTemplatesStore.getEmailTemplates(
            filter.value,
            pageIndex,
            order,
            column,
            true, // loadingOptionsChange
          );
        }

        if (emailTemplatesStore.getEmailTemplatesRPCError) {
          switch (emailTemplatesStore.getEmailTemplatesRPCError) {
            case GetEmailTemplatesRPC.RPCError.InvalidPageIndex:
              await emailTemplatesStore.getEmailTemplates(
                filter.value,
                Math.max(1, pageIndex - 1),
                order,
                column,
                true, // loadingOptionsChange
              );
              break;
            default:
              emailTemplatesStore.getEmailTemplatesRPCError satisfies never;
          }
          return [];
        }

        setTotalItems(emailTemplatesStore.totalItems);
        return emailTemplatesStore.emailTemplates.map((emailTemplate) => ({
          id: emailTemplate.id,
          cells: getEmailTemplateTableCells(emailTemplate),
        }));
      };

    return (
      <Stack gap={spacing.spaceSM} width="100%" maxWidth={TABLE_WIDTH}>
        <Table<EmailTemplateTableFilterType>
          name="emailTemplatesTable"
          ref={emailTemplateTableRef}
          header={emailTemplateTableHeaders}
          paginationOption={paginationOption}
          onTableOptionsChange={onTableOptionsChange}
          viewOverrides={viewOverrides}
          onRowClick={(row) =>
            setSearchParams((prev) => {
              prev.set(RouteSearchParam.TemplateId, row.id);
              prev.set(RouteSearchParam.Mode, Mode.View);
              return prev;
            })
          }
          leftPinnedColumnIds={[]}
          rightPinnedColumnIds={
            showActionButtons ? [EmailTemplatesTableColumn.Actions] : []
          }
          filterOption={{
            initialFilterValue: { value: EMPTY_CHARACTER },
            filterComponent: (filter, setFilter): React.ReactElement => (
              <EmailTemplateTableFilter filter={filter} setFilter={setFilter} />
            ),
          }}
        />
      </Stack>
    );
  },
);
